import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { formatApiErrorDetail } from "../lib/api";
import { Sparkles } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (user && user !== false) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-alabaster grain flex justify-center p-6 py-10">
      <div className="grid lg:grid-cols-2 gap-16 max-w-6xl w-full items-center my-auto">
        <div className="hidden lg:block">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-10 h-10 rounded-full bg-ink text-alabaster flex items-center justify-center">
              <Sparkles size={18} strokeWidth={1.6} />
            </div>
            <div className="font-serif text-2xl text-ink">Wisha</div>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.05] tracking-tight">
            Eure Wünsche.<br />Euer Raum.<br />
            <span className="italic text-terracotta">Euer Leben.</span>
          </h1>
          <p className="text-text_secondary text-lg mt-8 max-w-md leading-relaxed">
            Eine private Bucketlist für zwei. Sammelt Reisen, Date-Ideen,
            Geheimnisse und Gewohnheiten — und schaut zurück auf alles,
            was ihr schon erlebt habt.
          </p>
          <div className="flex flex-wrap gap-2 mt-10 max-w-md">
            {["Reisen", "Date-Ideen", "Traditionen", "Überraschungen", "Jahresziele"].map((t) => (
              <span key={t} className="text-xs uppercase tracking-[0.2em] text-text_secondary border border-border rounded-full px-3 py-1.5">{t}</span>
            ))}
          </div>
        </div>

        <div className="bg-white border border-border rounded-3xl p-8 sm:p-12">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-full bg-ink text-alabaster flex items-center justify-center">
              <Sparkles size={18} strokeWidth={1.6} />
            </div>
            <div className="font-serif text-2xl text-ink">Wisha</div>
          </div>

          <h2 className="font-serif text-3xl text-ink mb-2">Willkommen zurück</h2>
          <p className="text-text_secondary mb-8">Loggt euch ein und macht weiter, wo ihr aufgehört habt.</p>

          <form onSubmit={submit} className="space-y-6">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-text_secondary">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-transparent border-x-0 border-t-0 border-b border-border rounded-none px-0 py-3 focus-visible:ring-0 focus-visible:border-ink"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-text_secondary">Passwort</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-transparent border-x-0 border-t-0 border-b border-border rounded-none px-0 py-3 focus-visible:ring-0 focus-visible:border-ink"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-ink text-alabaster hover:bg-ink/90 py-6 text-base"
              data-testid="login-submit-button"
            >
              {loading ? "Anmelden …" : "Anmelden"}
            </Button>
          </form>

          <div className="mt-8 pt-8 border-t border-border text-center">
            <p className="text-sm text-text_secondary">Noch kein Account? </p>
            <Link to="/register" className="text-ink font-medium hover:underline" data-testid="link-to-register">
              Erstellt einen Couple-Space →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
